/* ============================================================
   Consent + GA constants and pure helpers — the single source of
   truth shared by BOTH analytics surfaces:

     1. the React SPA (consent.js / ga.js import from here), and
     2. the static blog (scripts/blog/gen-runtime.js inlines this
        file as text into build/blog/assets/blog.js).

   Because of (2), this file must stay consumable as plain script:
   ONLY top-level `export const` declarations — no `import`, no
   `export default`, no re-exports. gen-runtime.js strips the
   `export ` prefixes and asserts these invariants at build time.

   Both surfaces read and write the same localStorage record, so a
   choice made on the landing page suppresses the blog banner and
   vice versa. Changing key, version, shape, or expiry here changes
   it everywhere — which is the point.
   ============================================================ */

export const CONSENT_KEY = 'qombra-consent';
export const CONSENT_VERSION = 1;
export const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const GA_ID = 'G-08H4KFYPDY';

/**
 * The stored choice, or null when the banner must (re-)ask: nothing stored,
 * stored under an older version, or older than 12 months.
 * @returns {{analytics: boolean, ts: number, version: number} | null}
 */
export const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (stored.version !== CONSENT_VERSION) return null;
    if (typeof stored.analytics !== 'boolean') return null;
    if (!Number.isFinite(stored.ts) || Date.now() - stored.ts > MAX_AGE_MS) return null;
    return stored;
  } catch {
    return null;
  }
};

export const writeConsent = (analytics) => {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ analytics, ts: Date.now(), version: CONSENT_VERSION })
    );
  } catch {
    // Storage unavailable (private mode etc.) — the banner will simply re-ask.
  }
};

// Best-effort removal of Google Analytics cookies on withdrawal. GA sets them
// on the registrable domain (e.g. .qombra.com), so try both host variants.
export const clearGaCookies = () => {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = [host];
  if (parts.length >= 2) domains.push(`.${parts.slice(-2).join('.')}`);
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0].trim();
    if (name !== '_ga' && !name.startsWith('_ga_')) return;
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = `${name}=; ${expire}`;
    domains.forEach((d) => {
      document.cookie = `${name}=; ${expire}; domain=${d}`;
    });
  });
};
